// src/pages/VehicleV2/BatteryCardReport.jsx
// Slim table + view modal to stop horizontal overflow.

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/batteryCardReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr)
    .map((x) => ({
      label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
      value: String(x.value ?? x.id ?? x.key ?? x.label ?? fallbackLabel),
      ...x,
    }))
    .filter((x) => toStr(x.value).toLowerCase() !== "select" && toStr(x.label).toLowerCase() !== "select");

  return [{ label: fallbackLabel, value: fallbackLabel }, ...list];
};

const defaultFiltersByRole = (branchType, branchId) => ({
  fromDate: "",
  toDate: "",
  branch: toStr(branchType).toUpperCase() === "HO" ? "Select" : String(branchId),
  batteryCode: "Select",
  serialNo: "Select",
  batteryType: "Select",
  supplier: "Select",
  vehicleMainType: "Select",
  vehicleCode: "",
  vehicleName: "",
  vehicleRegNo: "",
  batteryStatus: "Select",
  batteryMake: "",
  status: "Select",
});

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return toStr(v);
  return `₹${n.toLocaleString("en-IN")}`;
};

export default function BatteryCardReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const didInitRef = useRef(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [batteryTypes, setBatteryTypes] = useState([{ label: "Select", value: "Select" }]);
  const [serialNos, setSerialNos] = useState([{ label: "Select", value: "Select" }]);
  const [batteryCodes, setBatteryCodes] = useState([{ label: "Select", value: "Select" }]);
  const [suppliers, setSuppliers] = useState([{ label: "Select", value: "Select" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([
    { label: "Select", value: "Select" },
  ]);

  const baseDefaultFilters = useMemo(
    () => defaultFiltersByRole(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const [filters, setFilters] = useState(baseDefaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const currentItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const inputHeadClass = useMemo(() => `${buttonClass} text-white`, [buttonClass]);

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border overflow-hidden ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const resetDependentDropdowns = useCallback(() => {
    setBatteryTypes([{ label: "Select", value: "Select" }]);
    setSerialNos([{ label: "Select", value: "Select" }]);
    setBatteryCodes([{ label: "Select", value: "Select" }]);
    setSuppliers([{ label: "Select", value: "Select" }]);
    setVehicleMainTypes([{ label: "Select", value: "Select" }]);
  }, []);

  const loadInit = useCallback(
    async (selectedBranch) => {
      try {
        setDropdownLoading(true);

        const params = {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
          selectedBranch,
        };

        const { data } = await axios.get(`${API_BASE}/init`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Init failed");
        }

        setBranches(normalizeOptionList(data.branches, "Select"));
        setBatteryTypes(normalizeOptionList(data.batteryTypes, "Select"));
        setSerialNos(normalizeOptionList(data.serialNos, "Select"));
        setBatteryCodes(normalizeOptionList(data.batteryCodes, "Select"));
        setSuppliers(normalizeOptionList(data.suppliers, "Select"));
        setVehicleMainTypes(normalizeOptionList(data.vehicleMainTypes, "Select"));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load dropdowns"
        );
        setBranches([{ label: "Select", value: "Select" }]);
        resetDependentDropdowns();
      } finally {
        setDropdownLoading(false);
      }
    },
    [session, commonHeaders, resetDependentDropdowns]
  );

  const fetchList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);

        const params = {
          branchType: session.branchType,
          branch: nextFilters.branch,
          fromDate: nextFilters.fromDate,
          toDate: nextFilters.toDate,
          batteryCode: nextFilters.batteryCode,
          serialNo: nextFilters.serialNo,
          batteryType: nextFilters.batteryType,
          supplier: nextFilters.supplier,
          vehicleMainType: nextFilters.vehicleMainType,
          vehicleCode: nextFilters.vehicleCode,
          vehicleName: nextFilters.vehicleName,
          vehicleRegNo: nextFilters.vehicleRegNo,
          batteryStatus: nextFilters.batteryStatus,
          batteryMake: nextFilters.batteryMake,
          status: nextFilters.status,
          loginUserId: session.loginUserId,
          branchId: session.branchId,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "List failed");
        }

        const list = toList(data.rows);
        setRows(list);
        setTotal(list.length);
        setPage(1);
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load report"
        );
        setRows([]);
        setTotal(0);
        setPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      const reset = defaultFiltersByRole(session.branchType, session.branchId);
      setFilters(reset);
      await loadInit(reset.branch);
      await fetchList(reset);
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadInit, fetchList]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (name === "branch") {
        const next = { ...filters, branch: value };
        setFilters(next);

        (async () => {
          try {
            await loadInit(value);
            setFilters((prev) => ({
              ...prev,
              branch: value,
              batteryCode: "Select",
              serialNo: "Select",
              batteryType: "Select",
              supplier: "Select",
              vehicleMainType: "Select",
            }));
          } catch (err) {
            console.error(err);
          }
        })();

        return;
      }

      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [filters, loadInit]
  );

  const handleSearch = useCallback(async () => {
    await fetchList(filters);
    toast.success("Search completed");
  }, [fetchList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = defaultFiltersByRole(session.branchType, session.branchId);
    setFilters(reset);
    await loadInit(reset.branch);
    await fetchList(reset);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, loadInit, fetchList]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleReset = useCallback(() => {
    const reset = defaultFiltersByRole(session.branchType, session.branchId);
    setFilters(reset);
    setRows([]);
    setTotal(0);
    setPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId]);

  const handleRowPrint = useCallback(
    (row) => {
      const batteryId = row?.batteryId || row?.fld_batteryid || row?.id;
      if (!batteryId) {
        toast.error("Battery ID missing");
        return;
      }

      navigate("/vehicle/BatteryCardReport_Print", {
        state: { batteryId },
      });
    },
    [navigate]
  );

  const handlePrintAll = useCallback(() => {
    navigate("/vehicle/BatteryCardReport_AllPrint", {
      state: { filters },
    });
  }, [navigate, filters]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goPage = useCallback(
    (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setPage(next);
    },
    [totalPages]
  );

  const handleExport = useCallback(() => {
    if (!rows.length) {
      toast.info("No data to export");
      return;
    }

    const cols = [
      "jobMainType",
      "vehicleCode",
      "vehicleName",
      "vehicleRegNo",
      "batteryCode",
      "batterySerialNumber",
      "batteryType",
      "batteryMake",
      "issuedBranch",
      "currentBranch",
      "purchaseAmount",
      "issueDate",
      "expectedLife",
      "warrantyPeriod",
      "supplier",
    ];

    const escapeCSV = (v) => {
      const s = String(v ?? "");
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const csv = [
      cols.join(","),
      ...rows.map((r) => cols.map((c) => escapeCSV(r[c])).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `BatteryCardReport_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success("Exported CSV");
  }, [rows]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 overflow-x-hidden ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Battery Card Report
              </h1>
              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {total}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || dropdownLoading ? (
            <div className="text-center py-16">
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
            </div>
          ) : (
            <>
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>To Date</label>
                      <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select name="branch" value={filters.branch} onChange={handleChange} className={inputClass} disabled={toStr(session.branchType).toUpperCase() !== "HO"}>
                        {branches.map((b, idx) => (
                          <option key={`${b.value}-${idx}`} value={b.value}>{b.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Battery Code</label>
                      <select name="batteryCode" value={filters.batteryCode} onChange={handleChange} className={inputClass}>
                        {batteryCodes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.label === "Select" ? "Select" : x.label}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Serial No</label>
                      <select name="serialNo" value={filters.serialNo} onChange={handleChange} className={inputClass}>
                        {serialNos.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.label === "Select" ? "Select" : x.label}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Battery Type</label>
                      <select name="batteryType" value={filters.batteryType} onChange={handleChange} className={inputClass}>
                        {batteryTypes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Supplier</label>
                      <select name="supplier" value={filters.supplier} onChange={handleChange} className={inputClass}>
                        {suppliers.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} className={inputClass}>
                        {vehicleMainTypes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={inputClass} placeholder="Enter vehicle code" />
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={inputClass} placeholder="Enter vehicle name" />
                    </div>
                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input type="text" name="vehicleRegNo" value={filters.vehicleRegNo} onChange={handleChange} className={inputClass} placeholder="Enter vehicle reg no" />
                    </div>
                    <div>
                      <label className={labelClass}>Battery Make</label>
                      <input type="text" name="batteryMake" value={filters.batteryMake} onChange={handleChange} className={inputClass} placeholder="Enter battery make" />
                    </div>
                    <div>
                      <label className={labelClass}>Battery Status</label>
                      <select name="batteryStatus" value={filters.batteryStatus} onChange={handleChange} className={inputClass}>
                        <option value="Select">Select</option>
                        <option value="All">All</option>
                        <option value="Installed">Installed</option>
                        <option value="Stock">Stock</option>
                        <option value="Scrap">Scrap</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Status</label>
                      <select name="status" value={filters.status} onChange={handleChange} className={inputClass}>
                        <option value="Select">Select</option>
                        <option value="All">All</option>
                        <option value="Active">Active</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {listLoading ? "Loading list..." : "Ready"}
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      <button onClick={handleReset} type="button" className={`font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Clear / Reset
                      </button>
                      <button onClick={handleViewAll} type="button" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold">
                        <FaEye /> View All
                      </button>
                      <button onClick={handleSearch} type="button" className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}>
                        <FaSearch /> Search
                      </button>
                      <button onClick={handlePrintAll} type="button" className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}>
                        <FaPrint /> Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <FaInfoCircle className="w-10 h-10 text-gray-400 mx-auto mb-4" />
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>No Records</h3>
                    <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>Use filters and click Search.</p>
                  </div>
                ) : (
                  <table className={`w-full table-fixed ${isDark ? "bg-gray-800" : "bg-white"}`}>
                    <thead className={inputHeadClass}>
                      <tr>
                        <th className="px-3 py-3 text-left text-sm w-14">S.No</th>
                        <th className="px-3 py-3 text-left text-sm">Vehicle Code</th>
                        <th className="px-3 py-3 text-left text-sm">Battery Code</th>
                        <th className="px-3 py-3 text-left text-sm">Serial No</th>
                        <th className="px-3 py-3 text-center text-sm w-16">View</th>
                        <th className="px-3 py-3 text-center text-sm w-16">Print</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {currentItems.map((r, idx) => (
                        <tr
                          key={r.batteryId ?? r.fld_batteryid ?? idx}
                          className={`${isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={`px-3 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td className={`px-3 py-3 text-sm truncate ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.vehicleCode ?? "-"}
                          </td>
                          <td className={`px-3 py-3 text-sm font-medium truncate ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {r.batteryCode ?? "-"}
                          </td>
                          <td className={`px-3 py-3 text-sm truncate ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.batterySerialNumber ?? "-"}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button type="button" onClick={() => setViewRow(r)} className="text-blue-500 hover:text-blue-600 text-lg" title="View">
                              <FaEye />
                            </button>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRowPrint(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass}`}
                              title="Print"
                            >
                              <FaPrint />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {total > 0 && (
                <div className="mt-6 flex justify-center items-center gap-3">
                  <button
                    className={`px-4 py-2 border rounded ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"} ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page - 1)}
                    disabled={page <= 1}
                    type="button"
                  >
                    Prev
                  </button>
                  <div className={`px-4 py-2 border rounded ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>
                    Page {page} / {totalPages}
                  </div>
                  <button
                    className={`px-4 py-2 border rounded ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"} ${page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page + 1)}
                    disabled={page >= totalPages}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {viewRow && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewRow(null)}>
              <div
                className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl border ${
                  isDark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-200 text-gray-900"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <div>
                    <h3 className="text-xl font-bold">Battery Card Details</h3>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {viewRow.batteryCode || viewRow.batterySerialNumber || "-"}
                    </p>
                  </div>
                  <button type="button" onClick={() => setViewRow(null)} className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}>
                    <FaTimes />
                  </button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["Job Main Type", viewRow.jobMainType],
                    ["Vehicle Code", viewRow.vehicleCode],
                    ["Vehicle Name", viewRow.vehicleName],
                    ["Vehicle Reg No", viewRow.vehicleRegNo],
                    ["Battery Code", viewRow.batteryCode],
                    ["Serial No", viewRow.batterySerialNumber],
                    ["Type", viewRow.batteryType],
                    ["Make", viewRow.batteryMake],
                    ["Issued Branch", viewRow.issuedBranch],
                    ["Carried Branch", viewRow.currentBranch],
                    ["Purchase Amount", fmtMoney(viewRow.purchaseAmount)],
                    ["Issued Date", viewRow.issueDate],
                    ["Expected Life", viewRow.expectedLife],
                    ["Warranty", viewRow.warrantyPeriod],
                    ["Supplier", viewRow.supplier],
                  ].map(([label, value]) => (
                    <div key={label} className={`p-3 rounded-lg border ${isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                      <div className={`text-xs font-semibold uppercase mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{label}</div>
                      <div className="text-sm font-medium break-words">{value || "-"}</div>
                    </div>
                  ))}
                </div>
                <div className={`flex justify-end gap-2 px-6 py-4 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <button type="button" onClick={() => setViewRow(null)} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200" : "border-gray-300 text-gray-700"}`}>
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const row = viewRow;
                      setViewRow(null);
                      handleRowPrint(row);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${buttonClass} ${hoverClass}`}
                  >
                    Print
                  </button>
                </div>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={2500} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
